import dayjs, { Dayjs } from "dayjs"
import {
  EAsnStatus,
  EAsnStatusType,
  IInstallCodeExtra,
  IInstallCode,
} from "./installCode.types"
import { formatDate } from "@/helpers/dateHelper"

const ASN_EXPIRY_YEARS = 3
const ASN_EXPIRY_WARNING_DAYS = 60
const UPLOAD_OUTCOME_PATTERN = /upload/i
const UNOBTAINABLE_OUTCOME_PATTERN = /unobtainable/i
export const UNOBTAINABLE_COMMENT = "unable to obtain install code"

export class InstallCode implements IInstallCode {
  constructor(
    public id: number,
    public msn: string,
    public createdAt: Dayjs,
    public updatedAt: Dayjs,
    public meterType?: string,
    public mpxn?: string,
    public asnDate?: Dayjs,
    public asnOutcome?: string,
    public comment?: string,
    public deviceId?: string,
    public installCode?: string,
    public lastDateChecked?: Dayjs,
    public mapId?: string,
    public mapName?: string,
    public requestedDate?: Dayjs,
  ) {}

  getAsnStatus(): EAsnStatusType {
    if (
      this.comment?.toLocaleLowerCase() === UNOBTAINABLE_COMMENT ||
      UNOBTAINABLE_OUTCOME_PATTERN.test(this.asnOutcome ?? "")
    )
      return EAsnStatus.UNOBTAINABLE
    if (!this.isAsnUploaded()) return EAsnStatus.MISSING
    if (!this.asnDate) return EAsnStatus.MISSING_DATE

    const expiryDate = this.asnDate.add(ASN_EXPIRY_YEARS, "year")
    const daysLeft = Math.ceil(
      expiryDate.diff(dayjs().startOf("day"), "day", true),
    )

    if (daysLeft < 0) return EAsnStatus.EXPIRED
    if (daysLeft <= ASN_EXPIRY_WARNING_DAYS) return EAsnStatus.EXPIRES_SOON

    return EAsnStatus.VALID
  }

  getAsnMessage(): string | null {
    const expiryDate = this.asnDate?.add(ASN_EXPIRY_YEARS, "year")

    switch (this.getAsnStatus()) {
      case EAsnStatus.MISSING:
        return "ASN not uploaded. Action required."
      case EAsnStatus.MISSING_DATE:
        return "ASN uploaded but no ASN Date found. Investigation required."
      case EAsnStatus.EXPIRED:
        return `ASN uploaded on ${formatDate(
          this.asnDate,
        )}. Expired on ${formatDate(expiryDate)}. Action required.`
      case EAsnStatus.EXPIRES_SOON:
        return `ASN uploaded on ${formatDate(
          this.asnDate,
        )}. Expires on ${formatDate(expiryDate)} — renewal required soon.`
      case EAsnStatus.UNOBTAINABLE:
        return `ASN unobtainable.`
      default:
        return `ASN uploaded on ${formatDate(
          this.asnDate,
        )}. Expires on ${formatDate(expiryDate)}.`
    }
  }

  private isAsnUploaded(): boolean {
    const outcome = this.asnOutcome ?? ""
    return UPLOAD_OUTCOME_PATTERN.test(outcome)
  }
}

export class InstallCodeExtra extends InstallCode implements IInstallCodeExtra {
  constructor(
    installCode: IInstallCode,
    public manufacturer?: string,
    public deviceModel?: string,
    public gbcsVersion?: string,
    public smetsVersion?: string,
    public firmwareVersion?: string,
  ) {
    super(
      installCode.id,
      installCode.msn,
      installCode.createdAt,
      installCode.updatedAt,
      installCode.meterType,
      installCode.mpxn,
      installCode.asnDate,
      installCode.asnOutcome,
      installCode.comment,
      installCode.deviceId,
      installCode.installCode,
      installCode.lastDateChecked,
      installCode.mapId,
      installCode.mapName,
      installCode.requestedDate,
    )
  }
}
